import React from 'react';
import {Controller, useForm} from 'react-hook-form';
import {
  Button,
  Grid,
  TextField,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import {IFdProductionCost} from 'constants/interfaces';

export type Submission = {
  id: string;
  year: number;
  plantingDate: string;
  numberOfHectares: number;
  seed: number;
  fertiliser: number;
  chemical: number;
  notes?: string;
};

export function FdProductionCostForm(props: {
  productionCost?: IFdProductionCost;
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (data: Submission) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  readonly?: boolean;
}) {
  const {handleSubmit, control} = useForm<Submission>({
    defaultValues: {
      id: props.productionCost?.id,
      year: props.productionCost?.year || new Date().getFullYear(),
      plantingDate: props.productionCost?.plantingDate
        ? props.productionCost.plantingDate.split('T')[0]
        : '',
      numberOfHectares: props.productionCost?.numberOfHectares,
      seed: props.productionCost?.seed,
      fertiliser: props.productionCost?.fertiliser,
      chemical: props.productionCost?.chemical,
      notes: props.productionCost?.notes || '',
    },
  });

  const onSubmit = (data: Submission) => props.onSubmit(data);

  return (
    <div className="generic-form-container production-cost-form">
      <div className="form-container" style={{width: '100%'}}>
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Production Cost Details
              </Typography>
            </Grid>

            {/* Planting date */}
            <Grid item xs={12}>
              <Controller
                name="plantingDate"
                control={control}
                rules={{required: 'Planting date is required'}}
                render={({field, fieldState: {error}}) => (
                  <TextField
                    {...field}
                    label="Planting Date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{shrink: true}}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* Number of hectares */}
            <Grid item xs={12}>
              <Controller
                name="numberOfHectares"
                control={control}
                rules={{required: 'Number of hectares is required'}}
                render={({field: {value, onChange}, fieldState: {error}}) => (
                  <TextField
                    label="Number of Hectares"
                    type="number"
                    variant="outlined"
                    value={value}
                    onChange={(e) => onChange(parseFloat(e.target.value))}
                    fullWidth
                    InputProps={{inputProps: {min: 0, step: 0.01}}}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* Seed cost */}
            <Grid item xs={12}>
              <Controller
                name="seed"
                control={control}
                rules={{required: 'Seed cost is required'}}
                render={({field: {value, onChange}, fieldState: {error}}) => (
                  <TextField
                    label="Seed Cost (Rands)"
                    type="number"
                    variant="outlined"
                    value={value}
                    onChange={(e) => onChange(parseFloat(e.target.value))}
                    fullWidth
                    InputProps={{inputProps: {min: 0, step: 0.01}}}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* Fertiliser cost */}
            <Grid item xs={12}>
              <Controller
                name="fertiliser"
                control={control}
                rules={{required: 'Fertiliser cost is required'}}
                render={({field: {value, onChange}, fieldState: {error}}) => (
                  <TextField
                    label="Fertiliser Cost (Rands)"
                    type="number"
                    variant="outlined"
                    value={value}
                    onChange={(e) => onChange(parseFloat(e.target.value))}
                    fullWidth
                    InputProps={{inputProps: {min: 0, step: 0.01}}}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* Chemical cost */}
            <Grid item xs={12}>
              <Controller
                name="chemical"
                control={control}
                rules={{required: 'Chemical cost is required'}}
                render={({field: {value, onChange}, fieldState: {error}}) => (
                  <TextField
                    label="Chemical Cost (Rands)"
                    type="number"
                    variant="outlined"
                    value={value}
                    onChange={(e) => onChange(parseFloat(e.target.value))}
                    fullWidth
                    InputProps={{inputProps: {min: 0, step: 0.01}}}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* Notes field */}
            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({field: {value, onChange}, fieldState: {error}}) => (
                  <TextField
                    label="Notes"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    rowsMax={6}
                    value={value || ''}
                    onChange={onChange}
                    placeholder="Enter any additional notes…"
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>
          </Grid>

          {!props.readonly && (
            <Grid container spacing={2} style={{marginTop: '1rem'}}>
              <Grid item xs={12} sm={6}>
                {props.cancelButtonTitle && (
                  <Button
                    className="secondary-btn"
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={props.isLoading}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  className="primary-btn"
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={props.isLoading}
                >
                  {props.isLoading ? (
                    <CircularProgress size={24} thickness={6} />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </div>
  );
}
